import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { cn, LoadingState, EmptyState, StatusChip, usePresence } from '@platform/ui-kit'
import type { ConfidentialityLevel } from '@platform/ui-kit'
import { api } from '@/lib/api'
import { MODULES, findModuleByKey } from '@/app/routes/moduleRegistry'
import { useAuth } from '@/app/providers/AuthProvider'

/* ------------------------------------------------------------------
   جست‌وجوی یکپارچه — سند مادر بند ۱۲.۳
   ------------------------------------------------------------------
   · فاز اول روی Index Projection در MySQL کار می‌کند، نه Elasticsearch.
   · فقط عنوان، شماره، نوع، وضعیت، مالک و مجوز ایندکس می‌شود.
   · محتوای محرمانه بدون سیاست جداگانه وارد Index نمی‌شود — بنابراین
     این پالت هرگز متن نامه محرمانه را نشان نمی‌دهد.
   ------------------------------------------------------------------ */

interface SearchHit {
  module: string
  entity_type: string
  entity_id: string
  title: string
  reference_no: string | null
  status: string | null
  confidentiality: ConfidentialityLevel
  deep_link: string
}

interface SearchPaletteProps {
  open: boolean
  onClose: () => void
}

export function SearchPalette({ open, onClose }: SearchPaletteProps) {
  const navigate = useNavigate()
  const { canSeeModule } = useAuth()
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [hits, setHits] = useState<SearchHit[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)

  /* بازنشانی هنگام باز شدن */
  useEffect(() => {
    if (!open) return
    setQuery('')
    setHits(null)
    setActiveIndex(0)
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0)
    return () => window.clearTimeout(timer)
  }, [open])

  /* قفل اسکرول پس‌زمینه */
  useEffect(() => {
    if (!open) return
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = overflow
    }
  }, [open])

  /* جست‌وجوی با تأخیر — از کوبیدن Backend جلوگیری می‌کند */
  useEffect(() => {
    if (!open) return

    const term = query.trim()
    if (term.length < 2) {
      setHits(null)
      setLoading(false)
      return
    }

    setLoading(true)
    const controller = new AbortController()

    const timer = window.setTimeout(async () => {
      try {
        const response = await api.get<{ data: SearchHit[] }>('/search', {
          params: { q: term, limit: 20 },
          signal: controller.signal,
        })
        setHits(response.data.data)
        setActiveIndex(0)
      } catch (error) {
        if (!controller.signal.aborted) setHits([])
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }, 280)

    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [query, open])

  /* پیمایش با صفحه‌کلید */
  useEffect(() => {
    if (!open) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (!hits || hits.length === 0) return

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setActiveIndex((index) => (index + 1) % hits.length)
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        setActiveIndex((index) => (index - 1 + hits.length) % hits.length)
      } else if (event.key === 'Enter') {
        event.preventDefault()
        go(hits[activeIndex])
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, hits, activeIndex, onClose])

  const go = (hit: SearchHit) => {
    onClose()
    navigate(hit.deep_link)
  }

  /* باز و بسته شدن با همان حرکتِ دیالوگ (lib/motion.ts در ui-kit) — پیش‌تر در یک فریم می‌آمد و می‌رفت */
  const { mounted, state } = usePresence(open)

  if (!mounted) return null

  return createPortal(
    <div
      className="fixed inset-0 flex items-start justify-center p-4 pt-[10vh]"
      style={{ zIndex: 'var(--pf-z-dialog)' }}
      inert={!open}
    >
      {/* پرده از توکن می‌آید تا با بقیه پنجره‌های شناور یکی باشد. */}
      <div
        data-state={state}
        className="pf-scrim absolute inset-0"
        style={{
          backgroundColor:
            'color-mix(in srgb, var(--pf-scrim) var(--pf-scrim-opacity), transparent)',
          backdropFilter: 'blur(var(--pf-scrim-blur))',
          WebkitBackdropFilter: 'blur(var(--pf-scrim-blur))',
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="جست‌وجوی یکپارچه"
        data-state={state}
        className="pf-pop relative flex w-full max-w-xl flex-col overflow-hidden rounded-[var(--pf-radius-xl)] border border-[var(--pf-border)] bg-[var(--pf-surface)] shadow-[var(--pf-shadow-xl)]"
      >
        <div className="flex items-center gap-2.5 border-b border-[var(--pf-border)] px-4">
          <svg viewBox="0 0 24 24" fill="none" className="size-4.5 shrink-0 text-[var(--pf-text-subtle)]" aria-hidden="true">
            <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.6" />
            <path d="m16 16 4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="نام، شماره نامه، شماره درخواست، عنوان جلسه…"
            className="h-13 flex-1 bg-transparent text-[length:var(--pf-text-base)] text-[var(--pf-text)] outline-none placeholder:text-[var(--pf-text-subtle)]"
          />
          <kbd className="rounded-[var(--pf-radius-sm)] border border-[var(--pf-border)] px-1.5 py-0.5 text-[length:var(--pf-text-2xs)] text-[var(--pf-text-subtle)]">
            Esc
          </kbd>
        </div>

        <div className="max-h-[52vh] overflow-y-auto">
          {loading && <LoadingState label="در حال جست‌وجو…" className="py-10" />}

          {!loading && query.trim().length < 2 && (
            <div className="px-4 py-5">
              <p className="mb-2.5 text-[length:var(--pf-text-2xs)] text-[var(--pf-text-subtle)]">
                رفتن سریع به ماژول
              </p>
              <div className="flex flex-wrap gap-1.5">
                {/* «فقط مدیر سیستم» اینجا هم فیلتر می‌شود — پرشِ سریع نباید راهِ
                    دورِ رسیدن به ماژولی باشد که از منو پنهان شده. */}
                {MODULES.filter(
                  (module) => module.enabled && canSeeModule(module),
                ).map((module) => (
                  <button
                    key={module.key}
                    type="button"
                    onClick={() => {
                      onClose()
                      navigate(`/app/${module.path}`)
                    }}
                    className="flex items-center gap-1.5 rounded-[var(--pf-radius)] border border-[var(--pf-border)] px-2.5 py-1.5 text-[length:var(--pf-text-xs)] text-[var(--pf-text-muted)] transition-colors hover:bg-[var(--pf-surface-hover)] hover:text-[var(--pf-text)]"
                  >
                    {module.icon}
                    {module.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!loading && hits?.length === 0 && (
            <EmptyState
              title="نتیجه‌ای یافت نشد"
              description="عبارت دیگری را امتحان کنید. توجه کنید که اسناد محرمانه در جست‌وجوی عمومی نمایش داده نمی‌شوند."
              illustration="/icons/3d/no-results.png"
              className="py-10"
            />
          )}

          {!loading &&
            hits?.map((hit, index) => {
              const module = findModuleByKey(hit.module)
              return (
                <button
                  key={`${hit.module}-${hit.entity_type}-${hit.entity_id}`}
                  type="button"
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => go(hit)}
                  className={cn(
                    'flex w-full items-center gap-3 px-4 py-2.5 text-start transition-colors',
                    index === activeIndex ? 'bg-[var(--pf-surface-hover)]' : 'bg-transparent',
                  )}
                >
                  <span className="text-[var(--pf-text-subtle)]">{module?.icon}</span>

                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[length:var(--pf-text-sm)] text-[var(--pf-text)]">
                      {hit.title}
                    </span>
                    <span className="block truncate text-[length:var(--pf-text-2xs)] text-[var(--pf-text-muted)]">
                      {module?.title}
                      {hit.reference_no && ` · ${hit.reference_no}`}
                    </span>
                  </span>

                  {hit.status && (
                    <StatusChip size="sm" dot={false} tone="neutral">
                      {hit.status}
                    </StatusChip>
                  )}
                </button>
              )
            })}
        </div>
      </div>
    </div>,
    document.body,
  )
}
